import {
  findSidebarCampaignAtPoint,
  isPointerOverSidebar,
  invalidateSidebarHitTestCache,
} from '../sidebarHitTest';
import {
  createEmptyState,
  getLockedDropTargetSprintId,
  getRawState,
  getRegistrations,
  setLockedDropTargetSprintId,
  setRawState,
} from './internalState';
import { attachPointerListeners, removePointerListeners } from './pointerListeners';
import { clearLastNotifiedSnapshot, getState, notify } from './subscriptions';
import { clearSidebarRowHighlights, resetVisualSyncTracking, syncDragVisualClasses } from './visualSync';

const resetState = () => {
  removePointerListeners();
  setLockedDropTargetSprintId(null);
  clearLastNotifiedSnapshot();
  setRawState(createEmptyState());
  syncDragVisualClasses();
  clearSidebarRowHighlights();
  resetVisualSyncTracking();
};

export function startMissionToSidebarDrag({
  taskId,
  sourceSprintId,
  taskName,
  taskIcon,
  source,
  sourceTask = null,
  sourceNodePositions = null,
  nestedSprintId = null,
}) {
  if (!taskId || !sourceSprintId) {
    console.warn('[M2S] start REJECTED — missing taskId or sourceSprintId', { taskId, sourceSprintId });
    return;
  }

  console.log('[M2S] drag start', {
    taskId,
    taskName,
    sourceSprintId,
    source,
    hasSourceTaskSnapshot: !!sourceTask,
    nestedSprintId,
  });

  removePointerListeners();
  setLockedDropTargetSprintId(null);
  setRawState({
    active: true,
    taskId,
    sourceSprintId,
    taskName: taskName || '',
    taskIcon: taskIcon ?? null,
    source: source || null,
    hoverTargetSprintId: null,
    isOverSidebar: false,
    lastPointer: null,
    sourceTask: sourceTask ? { ...sourceTask } : null,
    sourceNodePositions: sourceNodePositions ? { ...sourceNodePositions } : null,
    nestedSprintId: nestedSprintId || null,
  });

  // Fresh geometry for this drag (rects are cached during the drag for performance)
  invalidateSidebarHitTestCache();

  attachPointerListeners(
    (e) => updateMissionToSidebarPointer(e.clientX, e.clientY),
    (e) => updateMissionToSidebarPointer(e.clientX, e.clientY, { isFinal: true }),
  );
  notify();
}

/**
 * @param {number} clientX
 * @param {number} clientY
 * @param {{ isFinal?: boolean }} [options] - isFinal marks release (mouseup/pointerup)
 *   coordinates. Final updates may only ADD/refine a drop target, never clear one:
 *   the release point often overshoots the hovered row (or the sidebar itself) by a
 *   few pixels compared to the last mousemove, and clearing here made valid drops
 *   snap back.
 */
export function updateMissionToSidebarPointer(clientX, clientY, { isFinal = false } = {}) {
  const state = getRawState();
  if (!state.active) return;

  // pointermove + mousemove both fire per physical move (and callers may re-send
  // the same coords) — skip exact duplicates. Final updates always process.
  if (
    !isFinal
    && state.lastPointer
    && state.lastPointer.x === clientX
    && state.lastPointer.y === clientY
  ) {
    return;
  }

  state.lastPointer = { x: clientX, y: clientY };
  state.isOverSidebar = isPointerOverSidebar(clientX, clientY);
  const targetId = findSidebarCampaignAtPoint(clientX, clientY);

  if (isFinal) {
    console.log('[M2S] final pointer update', {
      clientX,
      clientY,
      isOverSidebar: state.isOverSidebar,
      hitTestTargetId: targetId,
      prevHoverTarget: state.hoverTargetSprintId,
      prevLockedTarget: getLockedDropTargetSprintId(),
    });
  }

  // Invalid targets: the campaign we came from, and (for container nodes) the
  // campaign the dragged node itself contains — nesting into it would be circular
  const isInvalidTarget =
    targetId === state.sourceSprintId
    || (!!state.nestedSprintId && targetId === state.nestedSprintId);

  if (targetId && isInvalidTarget) {
    // Squarely over an invalid target — definitively not a drop target,
    // so clear even on final updates (no overshoot ambiguity here)
    state.hoverTargetSprintId = null;
    setLockedDropTargetSprintId(null);
  } else if (targetId) {
    state.hoverTargetSprintId = targetId;
    setLockedDropTargetSprintId(targetId);
  } else if (!isFinal) {
    // Between rows (still over sidebar): keep the locked target; off the sidebar: clear it
    state.hoverTargetSprintId = null;
    if (!state.isOverSidebar) {
      setLockedDropTargetSprintId(null);
    }
  }

  notify();
}

/**
 * Complete drop when pointer is over a valid sidebar campaign.
 * @param {number} [clientX] - Optional final pointer X (mouseup) for one last hit-test
 * @param {number} [clientY] - Optional final pointer Y (mouseup) for one last hit-test
 * @returns {{ taskId: string, sourceSprintId: string, targetSprintId: string }|null}
 */
export function tryCompleteMissionToSidebarDrop(clientX, clientY) {
  const state = getRawState();
  if (!state.active) {
    console.log('[M2S] tryComplete called but drag not active — nothing to do');
    return null;
  }

  // Preserve targets as a safety net (final updates never clear, but keep the fallbacks)
  const savedHoverTarget = state.hoverTargetSprintId;
  const savedLockedTarget = getLockedDropTargetSprintId();
  const savedOverSidebar = state.isOverSidebar;

  if (clientX != null && clientY != null) {
    updateMissionToSidebarPointer(clientX, clientY, { isFinal: true });
  }

  const targetSprintId =
    state.hoverTargetSprintId
    || getLockedDropTargetSprintId()
    || savedHoverTarget
    || savedLockedTarget
    || (savedOverSidebar ? savedLockedTarget : null);

  console.log('[M2S] tryComplete resolving target', {
    hoverTarget: state.hoverTargetSprintId,
    lockedTarget: getLockedDropTargetSprintId(),
    savedHoverTarget,
    savedLockedTarget,
    savedOverSidebar,
    resolvedTargetSprintId: targetSprintId,
    sourceSprintId: state.sourceSprintId,
  });

  if (!targetSprintId || targetSprintId === state.sourceSprintId) {
    console.warn('[M2S] DROP CANCELLED —', !targetSprintId
      ? 'no drop target resolved'
      : 'target is the source campaign');
    cancelMissionToSidebarDrag();
    return null;
  }

  const payload = {
    taskId: state.taskId,
    sourceSprintId: state.sourceSprintId,
    targetSprintId,
    nestedSprintId: state.nestedSprintId || null,
    taskName: state.taskName || null,
  };

  const registrations = getRegistrations();
  let moveOk = true;
  if (registrations.moveHandler) {
    moveOk = registrations.moveHandler(payload) !== false;
  } else {
    console.warn('[M2S] DROP CANCELLED — no moveHandler registered');
    moveOk = false;
  }

  if (!moveOk) {
    console.warn('[M2S] DROP CANCELLED — moveHandler returned false', payload);
    cancelMissionToSidebarDrag();
    return null;
  }

  console.log('[M2S] drop COMPLETED, move handed off', payload);
  resetState();
  notify();
  return payload;
}

export function cancelMissionToSidebarDrag() {
  const state = getRawState();
  if (!state.active) return;
  console.log('[M2S] drag cancelled/reset', { taskId: state.taskId, calledFrom: new Error().stack?.split('\n')[2]?.trim() });
  resetState();
  notify();
}

/** Clear a stuck flowchart canvas pointer-events lock from an interrupted drag */
export function restoreFlowchartCanvasPointerEvents() {
  document.querySelectorAll('.flowchart__canvas').forEach((canvas) => {
    if (canvas.style.pointerEvents === 'none') {
      canvas.style.pointerEvents = '';
    }
  });
}

export { getState };
